import { useEffect, useRef, useState } from "react";
import type { CatalogImageLoadDiagnostic } from "../types";

interface CatalogImageLoadInfoProps {
  diagnostics?: CatalogImageLoadDiagnostic[];
  showSupport?: boolean;
  sellerSku?: string;
  asin?: string | null;
  marketplaceCode?: string;
}

export function CatalogImageLoadInfo({ diagnostics = [], sellerSku, asin, marketplaceCode, showSupport = true }: CatalogImageLoadInfoProps) {
  const last = diagnostics.at(-1);
  const recovered = last?.outcome === "ready" && diagnostics.some(attempt => attempt.outcome === "failed");
  if (!last || last.outcome === "ready" && !recovered) return null;
  const notFound = last.outcome === "failed" && last.httpStatus === 404;
  return <div className="review-catalog-load-info" role={recovered ? "status" : "alert"}>
    <p>{notFound ? `Katalogeintrag für ASIN ${asin || "unbekannt"} in ${marketplaceCode || "unbekanntem Land"} nicht gefunden (HTTP 404). Prüfe die SKU/ASIN-Zuordnung in Seller Central. ` : ""}{recovered ? "Katalogbilder erfolgreich separat geladen." : `Katalogabruf fehlgeschlagen: ${last.message}`}{!recovered && last.httpStatus != null && ` · HTTP ${last.httpStatus}`}{!recovered && last.amazonCodes?.length ? ` · Amazon-Code: ${last.amazonCodes.join(", ")}` : ""}</p>
    {notFound && showSupport && <CatalogNotFoundSupport diagnostics={diagnostics} sellerSku={sellerSku} asin={asin} marketplaceCode={marketplaceCode}/>}
    <details><summary>Details zum Bildabruf</summary><ul>{diagnostics.map((attempt, index) => <li key={index}>
      <strong>{attempt.source === "images-only" ? "Gezielter Bilderabruf" : "Standard-Katalogabruf"}: {attempt.outcome === "ready" ? "geladen" : "fehlgeschlagen"}</strong>
      <p>{attempt.message}{attempt.httpStatus != null && ` · HTTP ${attempt.httpStatus}`}{attempt.amazonCodes?.length ? ` · Amazon-Code: ${attempt.amazonCodes.join(", ")}` : ""}</p>
      {attempt.requestId && <p>Amazon-Anfrage-ID: {attempt.requestId}</p>}
    </li>)}</ul></details>
  </div>;
}

const SUPPORT_FIELDS = [
  { key: "request", label: "Wobei benötigen Sie Hilfe?", button: "Anliegen kopieren", rows: 5 },
  { key: "steps", label: "Welche Schritte haben Sie bereits unternommen?", button: "Bisherige Schritte kopieren", rows: 4 },
  { key: "references", label: "Relevante Referenznummern", button: "Referenzen kopieren", rows: 2 },
] as const;
type SupportField = typeof SUPPORT_FIELDS[number]["key"];
const singleLine = (value: string) => value.replace(/[\r\n]+/g, "; ").trim();
const sourceLabel = (attempt: CatalogImageLoadDiagnostic) => attempt.source === "images-only" ? "Gezielter Bilderabruf" : "Standard-Katalogabruf";

function CatalogNotFoundSupport({ diagnostics = [], sellerSku, asin, marketplaceCode }: CatalogImageLoadInfoProps) {
  // Only the existing safe diagnostic contract contributes to text or identity.
  const evidence = diagnostics.map(({ source, outcome, message, httpStatus, requestId, amazonCodes }) => ({ source, outcome, message, httpStatus, requestId, amazonCodes }));
  const identity = JSON.stringify([sellerSku, asin, marketplaceCode, evidence]);
  const article = `SKU: ${singleLine(sellerSku || "nicht verfügbar")}; ASIN: ${singleLine(asin || "nicht verfügbar")}; Land: ${singleLine(marketplaceCode || "nicht verfügbar")}`;
  const initial = () => ({
    identity,
    fields: {
      request: `Bitte untersuchen Sie den Katalogabruf und die SKU/ASIN-Zuordnung für ${article}. Der letzte Katalogabruf für diese ASIN und dieses Land ist mit HTTP 404 fehlgeschlagen. Bitte prüfen Sie die Zuordnung des Angebots und den Katalogeintrag.`,
      steps: `Der Manager hat die Katalogdaten für ${article} abgerufen.\n${evidence.map(attempt => `${sourceLabel(attempt)}: ${attempt.outcome === "ready" ? "geladen" : "fehlgeschlagen"}${attempt.httpStatus != null ? ` (HTTP ${attempt.httpStatus})` : ""}.`).join("\n")}`,
      references: [article, ...evidence.flatMap(attempt => [
        ...(attempt.requestId ? [`${sourceLabel(attempt)}: Amazon-Anfrage-ID ${singleLine(attempt.requestId)}`] : []),
        ...(attempt.amazonCodes?.length ? [`${sourceLabel(attempt)}: Amazon-Code ${attempt.amazonCodes.map(singleLine).join(", ")}`] : []),
      ])].join("; "),
    },
    copyMessage: "",
    manualCopy: "" as SupportField | "",
  });
  const [state, setState] = useState(initial);
  const current = state.identity === identity ? state : initial();
  const mounted = useRef(true), sequence = useRef(0);
  const live = useRef({ identity, current });
  live.current = { identity, current };
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; sequence.current++; };
  }, []);
  useEffect(() => {
    sequence.current++;
    setState(previous => previous.identity === identity ? previous : initial());
  }, [identity]);

  const copy = async (field: SupportField) => {
    if (!mounted.current || live.current.identity !== identity) return;
    const content = live.current.current.fields[field], request = ++sequence.current;
    setState(previous => ({ ...previous, copyMessage: "", manualCopy: "" }));
    try {
      await navigator.clipboard.writeText(content);
      if (mounted.current && live.current.identity === identity && sequence.current === request) setState(previous => ({ ...previous, copyMessage: `${SUPPORT_FIELDS.find(entry => entry.key === field)!.label}: kopiert.`, manualCopy: "" }));
    } catch {
      if (mounted.current && live.current.identity === identity && sequence.current === request) setState(previous => ({ ...previous, copyMessage: "Automatisches Kopieren ist nicht verfügbar. Wähle den Text unten und kopiere ihn mit Strg+C.", manualCopy: field }));
    }
  };
  return <section className="catalog-image-support catalog-image-support__draft" aria-label="Ausfüllhilfe zum fehlenden Katalogeintrag">
    <h3>Katalogabruf in Seller Central klären</h3>
    <p>{article}</p>
    <p>Prüfe die Zuordnung in Seller Central. Die Ausfüllhilfe bereitet nur Text vor. Prüfe und ergänze die Angaben und sende die Anfrage selbst bei Amazon ab. Lade die aktuellen Amazon-Daten danach im Manager erneut.</p>
    <a className="button button--secondary" href="https://sellercentral.amazon.de/help/center?redirectSource=Hill" target="_blank" rel="noopener noreferrer">Seller Central-Hilfe öffnen</a>
    {SUPPORT_FIELDS.map((entry, index) => <div className="catalog-image-support__field" key={entry.key}>
      <label className="catalog-image-support__text"><strong>{index + 1}. {entry.label}</strong>
        <textarea aria-label={entry.label} rows={entry.rows} value={current.fields[entry.key]} onChange={event => {
          if (!mounted.current || live.current.identity !== identity) return;
          sequence.current++;
          const value = entry.key === "references" ? event.target.value.replace(/[\r\n]+/g, "; ") : event.target.value;
          const latest = live.current.current;
          setState({ ...latest, fields: { ...latest.fields, [entry.key]: value }, copyMessage: "", manualCopy: "" });
        }}/>
      </label>
      <button type="button" className="button button--secondary" disabled={!current.fields[entry.key].trim()} onClick={() => copy(entry.key)}>{entry.button}</button>
    </div>)}
    {current.copyMessage && <p role="status">{current.copyMessage}</p>}
    {current.manualCopy && <textarea className="catalog-image-support__manual-copy" aria-label="Text zum manuellen Kopieren" rows={6} readOnly value={current.fields[current.manualCopy]} onFocus={event => event.currentTarget.select()}/>}
  </section>;
}
